import React, { useState } from 'react'
import { StaticImage } from 'gatsby-plugin-image'
import { TabPanel, Tabs, TabsBody } from '@material-tailwind/react'
import { PageProps } from 'gatsby'
import { FirmCTA } from '../FirmCTA'
import { FirmTemplateProps } from '../firmTemplate'
import { TeaserProps } from '../teaser'
import { ModalWithMedias } from '../../ui/medias/ModalWithMedia'
import { OfficesAddressesCard } from '../firm/OfficesAddressesCard'
import FirmMap from '../firm/map'
import { OtherFirms } from '../firm/OtherFirms'
import { TabsHeaderCommon } from '../../common/tabs/TabsHeaderCommon'
import { getDefaultActiveTab } from '../../../Utils/functionnals'
import { getIntershipAndAlternanceOffers } from '../../../Utils/offers/contractTypes'
import FirmOfferTemplate from '../firm/firmOfferTemplate'

export const FreeTemplate = ({
  constants,
  data,
  othersFirms,
  location,
  youTubeApiKey,
}: {
  constants: FirmTemplateProps['constants']
  data: FirmTemplateProps['data']
  othersFirms: TeaserProps[]
  location: PageProps['location']
  youTubeApiKey: string
}) => {
  const [activeTab, setActiveTab] = useState<string>(
    getDefaultActiveTab(location) || 'profil',
  )

  const [focusedAddress, setFocusedAddress] = useState<{
    street: string
    city: string
    postalCode: string
  } | null>(null)


  const tabsTitle: {
    label: string
    key: string
    pill?: number
  }[] = [
      {
        label: 'Profil',
        key: 'profil',
      },
    ]

  const offers = getIntershipAndAlternanceOffers(data.offers || [])

  if (offers?.length) {
    const offersLabel = offers.length === 1 ? 'Offre' : 'Offres'
    tabsTitle.push({
      label: offersLabel,
      key: 'offres',
      pill: offers.length,
    })
  }

  return (
    <section className="w-full max-w-[500px] md:max-w-[1000px] xl:max-w-[1115px] lg:max-w-[950px] lg:mx-auto 2xl:max-w-[1320px] my-6 md:mt-32 space-y-12 lg:space-y-16 mx-auto px-4 md:px-0">
      <Tabs value={activeTab}>
        <TabsHeaderCommon
          tabsTitle={tabsTitle}
          setActiveTab={setActiveTab}
          color={data?.colorCareerSite || undefined}
        />
        <TabsBody className='p-0'>
          <TabPanel value="profil" className="p-0 mt-4 lg:mt-8">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="md:col-span-2">
                <FirmImage
                  headerUrl={data.headerUrl}
                  mainMedia={data.mainMedia}
                  youTubeApiKey={youTubeApiKey}
                />
              </div>
              <FirmMap
                offices={data.offices}
                googleMapsApiKey={constants.googleMapsApiKey || ''}
                focusedAddress={focusedAddress}
                height={500}
              />

              <OfficesAddressesCard
                offices={data.offices || []}
                setFocusedAddress={setFocusedAddress}
                additionalClasses="max-h-[500px]"
              />
            </div>
          </TabPanel>

          <TabPanel value="offres" className="p-0 py-4 mt-4 lg:mt-8">
            <FirmOfferTemplate
              offers={
                offers?.map((offer) => ({
                  ...offer,
                  jobType: offer.jobType?.value,
                  companyName: data.name,
                  url: `/cabinets/${data.slug}/offres/${offer.slug}`,
                  city: offer.office.city || undefined,
                  salary: offer.payRange || undefined,
                  startDate: offer.startDate || undefined,
                  headerImage:
                    data.headerUrl ||
                    data.header ||
                    undefined,
                  logo:
                    data?.logoUrl || data?.logo || undefined,
                  title: offer.title,
                  color: data?.colorCareerSite || undefined,
                  contractType: offer.contractType || undefined,
                })) || []
              }
              color={data?.colorCareerSite || undefined}
            />
          </TabPanel>
        </TabsBody>
      </Tabs>

      <FirmCTA
        data={data}
        color={data?.colorCareerSite || undefined}
        title={`Envie de rejoindre ${data.name} ?`}
        buttonLabel="Postuler"
      />

      <OtherFirms
        othersFirms={othersFirms}
        title="D'autres cabinets pourraient vous intéresser"
      />
    </section>
  )
}

const FirmImage = ({
  headerUrl,
  mainMedia,
  youTubeApiKey,
}: {
  headerUrl?: string | null
  mainMedia?: FirmTemplateProps['data']['mainMedia']
  youTubeApiKey: string
}) => {
  if (mainMedia) {
    return (
      <ModalWithMedias
        id={mainMedia.id}
        media={mainMedia.media}
        name="Vidéo de présentation du cabinet"
        video={mainMedia.video}
        youTubeApiKey={youTubeApiKey}
        forceHeight
      />
    )
  }

  const classNames = 'h-full w-full object-cover rounded-xl shadow-xl'

  if (headerUrl) {
    return (
      <img src={headerUrl} alt="couverture du cabinet" className={classNames} />
    )
  }

  return (
    <StaticImage
      src="../../../../static/default-header-media.jpg"
      alt="header lamacompta"
      placeholder="blurred"
      layout="constrained"
      className={classNames}
    />
  )
}
